import { useParams, Link } from "react-router-dom";
import { blogPosts } from "../data/blogs";
import { motion, useScroll, useSpring } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { useEffect, useMemo, useState } from "react";

// Reading time
function getReadingTime(text) {
  const words = text.split(/\s+/).length;
  return `${Math.ceil(words / 200)} min read`;
}

// Extract headings + ids
function extractHeadings(md) {
  return md
    .split("\n")
    .filter((l) => l.startsWith("## "))
    .map((l) => {
      const text = l.replace("## ", "");
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      return { text, id };
    });
}

export default function BlogDetail() {
  const { slug } = useParams();
  const index = blogPosts.findIndex((p) => p.slug === slug);
  const post = blogPosts[index];
  const nextPost = blogPosts[index + 1];

  const headings = useMemo(() => extractHeadings(post?.content || ""), [post]);
  const readingTime = post ? getReadingTime(post.content) : "";

  // Scroll progress
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  // Active TOC highlight
  const [active, setActive] = useState("");
  useEffect(() => {
    const handler = () => {
      const offsets = headings.map((h) => {
        const el = document.getElementById(h.id);
        return el ? { id: h.id, top: el.getBoundingClientRect().top } : null;
      }).filter(Boolean);

      const current = offsets.find((o) => o.top > 0 && o.top < 200);
      if (current) setActive(current.id);
    };

    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, [headings]);

  if (!post) return <div className="min-h-screen flex items-center justify-center">Not found</div>;

  return (
    <section className="relative py-24 bg-slate-50">

      {/* Scroll Progress Bar */}
      <motion.div style={{ scaleX }} className="fixed top-0 left-0 right-0 h-1 bg-blue-600 origin-left z-50" />

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-4 gap-12">

        {/* MAIN */}
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3">

          <Link to="/blog" className="text-slate-500 mb-8 inline-block">← Back</Link>

          <h1 className="text-4xl md:text-5xl font-semibold mb-4">{post.title}</h1>

          <div className="flex gap-4 text-sm text-slate-500 mb-6">
            <span>{new Date(post.created_at).toLocaleDateString()}</span>
            <span>•</span>
            <span>{readingTime}</span>
          </div>

          {/* Tags */}
          <div className="flex gap-2 flex-wrap mb-10">
            {post.tags?.map((t, i) => (
              <span key={i} className="text-xs bg-blue-100 text-blue-600 px-3 py-1 rounded-full">{t}</span>
            ))}
          </div>

          {post.cover_image && (
            <img src={post.cover_image} className="rounded-2xl mb-12" />
          )}

          {/* Timeline + Markdown */}
          <div className="relative border-l border-slate-200 pl-6">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
              components={{
                h2: ({ children }) => {
                  const text = String(children);
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  return (
                    <h2 id={id} className="text-2xl font-semibold mt-12 mb-4 scroll-mt-24">{children}</h2>
                  );
                },
                pre: ({ children }) => (
                  <div className="relative group">
                    <pre className="bg-slate-900 text-slate-200 p-4 rounded-xl overflow-x-auto">{children}</pre>
                    <button
                      onClick={() => navigator.clipboard.writeText(children.props.children)}
                      className="absolute top-2 right-2 text-xs bg-white px-2 py-1 rounded opacity-0 group-hover:opacity-100"
                    >
                      Copy
                    </button>
                  </div>
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Next */}
          {nextPost && (
            <div className="mt-20 pt-10 border-t">
              <p className="text-sm text-slate-500">Next</p>
              <Link to={`/blog/${nextPost.slug}`} className="text-lg text-blue-600 font-semibold hover:underline">
                {nextPost.title} →
              </Link>
            </div>
          )}

        </motion.div>

        {/* TOC */}
        <div className="hidden lg:block">
          <div className="sticky top-24">
            <p className="text-sm font-semibold mb-4 text-slate-500">On this page</p>
            <ul className="space-y-2 text-sm">
              {headings.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className={`block hover:text-blue-600 ${active === h.id ? "text-blue-600 font-medium" : "text-slate-600"}`}
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>
    </section>
  );
}
